export type WebsiteConsent = {
  analytics: boolean;
  marketing: boolean;
};

export const WEBSITE_CONSENT_KEY = "greenswap-web-consent-v1";
export const WEBSITE_CONSENT_EVENT = "greenswap:web-consent-changed";

const SESSION_KEY = "greenswap_web_session_id";

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: (...args: unknown[]) => void;
  }
}

export function getWebsiteConsent(): WebsiteConsent | null {
  try {
    const raw = window.localStorage.getItem(WEBSITE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WebsiteConsent>;
    return { analytics: parsed.analytics === true, marketing: parsed.marketing === true };
  } catch {
    return null;
  }
}

export function saveWebsiteConsent(choice: WebsiteConsent): void {
  window.localStorage.setItem(WEBSITE_CONSENT_KEY, JSON.stringify(choice));
  window.dispatchEvent(new CustomEvent(WEBSITE_CONSENT_EVENT, { detail: choice }));
  updateGa4Consent(choice);
  if (choice.analytics) initGa4();
}

function resolveMeasurementId(): string | undefined {
  const override = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
  if (override) return override;
  if (window.location.hostname === "greenswap.us" || window.location.hostname === "www.greenswap.us") {
    return "G-7W0Q344V14";
  }
  return undefined;
}

const measurementId = resolveMeasurementId();
let ga4Initialized = false;

function gtag(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function ga4Consent(choice: WebsiteConsent): Record<string, "granted" | "denied"> {
  const analytics = choice.analytics ? "granted" : "denied";
  const marketing = choice.marketing ? "granted" : "denied";
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  };
}

export function initGa4(): void {
  const consent = getWebsiteConsent();
  if (!measurementId || !consent?.analytics || ga4Initialized) return;
  ga4Initialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => gtag(...args);
  gtag("js", new Date());
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("consent", "update", ga4Consent(consent));
  gtag("config", measurementId, {
    send_page_view: false,
    linker: { domains: ["greenswap.us", "app.greenswap.us"] },
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

export function updateGa4Consent(choice: WebsiteConsent): void {
  if (!measurementId || !ga4Initialized) return;
  gtag("consent", "update", ga4Consent(choice));
}

function sessionId(): string {
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const created = window.crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(SESSION_KEY, created);
  return created;
}

export function trackWebsiteEvent(eventType: string, eventData: Record<string, string>): void {
  if (!getWebsiteConsent()?.analytics) return;
  const event = {
    sessionId: sessionId(),
    eventType,
    eventData,
    page: window.location.pathname,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
  };
  void fetch("/api/telemetry/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify([event]),
    keepalive: true,
  }).catch(() => undefined);
}

export function trackGa4Event(name: string, params: Record<string, string>): void {
  if (!measurementId || !getWebsiteConsent()?.analytics) return;
  initGa4();
  gtag("event", name, params);
}

export function trackWebsitePageView(): void {
  if (!getWebsiteConsent()?.analytics) return;
  trackWebsiteEvent("page_view", { page: window.location.pathname });
  initGa4();
  if (measurementId) gtag("event", "page_view", { page_path: window.location.pathname });
}
